import React, { useRef, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Sparkles, Copy, Check, Send, RotateCcw } from "lucide-react";
import { toast } from "sonner";

const PRESETS = [
  {
    label: "Notícia completa",
    prompt: 'Escreva uma notícia para o site sobre: "[descreva o tema aqui]". Entregue: TÍTULO, RESUMO (1-2 frases) e CONTEÚDO (3-5 parágrafos).',
  },
  {
    label: "Descrição de produto",
    prompt: 'Crie uma descrição comercial e calorosa para o produto: "[nome do produto]". Inclua: o que é, como é feito, quem produz e por que vale a pena. Tamanho: 4-6 frases.',
  },
  {
    label: "3 ideias de manchete",
    prompt: 'Me dê 3 sugestões de manchete em formato lista para uma notícia sobre "[tema]". Tom jornalístico, caloroso, autêntico amazônico.',
  },
  {
    label: "Post para Instagram",
    prompt: 'Escreva um post para o Instagram @coomflona.oficial sobre "[tema]". Inclua texto principal (até 3 parágrafos curtos) e 6 hashtags relevantes.',
  },
  {
    label: "Reescrever / melhorar",
    prompt: 'Reescreva e melhore o seguinte texto para o site da cooperativa, mantendo o sentido mas com tom mais natural e amazônico:\n\n"""\n[cole o texto aqui]\n"""',
  },
];

export default function AdminAIHelper() {
  const [input, setInput] = useState("");
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [copiedIdx, setCopiedIdx] = useState(null);
  const sessionRef = useRef(`admin-${Date.now()}-${Math.random().toString(36).slice(2)}`);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setHistory((h) => [...h, { role: "user", content: text }]);
    setInput("");
    setLoading(true);
    try {
      const { data } = await api.post("/chat", {
        message: text,
        session_id: sessionRef.current,
        mode: "admin",
      });
      setHistory((h) => [...h, { role: "assistant", content: data.reply }]);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const copy = async (txt, idx) => {
    try {
      await navigator.clipboard.writeText(txt);
      setCopiedIdx(idx);
      toast.success("Copiado!");
      setTimeout(() => setCopiedIdx(null), 1500);
    } catch {
      toast.error("Não foi possível copiar.");
    }
  };

  const reset = () => {
    setHistory([]);
    sessionRef.current = `admin-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  };

  return (
    <div className="mt-8 grid lg:grid-cols-3 gap-6" data-testid="admin-ai-helper">
      <aside className="lg:col-span-1 space-y-3">
        <div className="bg-forest-700 text-cream-100 p-5">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-5 h-5 text-copaiba-300" />
            <div className="font-display text-xl">Assistente de redação</div>
          </div>
          <p className="text-cream-100/80 text-sm leading-relaxed">
            Use o GPT-5.2 para escrever notícias, descrições de produtos, manchetes e posts. Pode editar o texto pronto e
            colar nos formulários de Notícias e Produtos.
          </p>
        </div>
        <div className="bg-white border border-cream-400 p-5">
          <div className="text-xs uppercase tracking-[0.18em] text-forest-900/60 mb-3">Atalhos prontos</div>
          <div className="space-y-2">
            {PRESETS.map((p) => (
              <button
                key={p.label}
                type="button"
                onClick={() => setInput(p.prompt)}
                data-testid={`ai-preset-${p.label.replace(/\s/g, "-")}`}
                className="w-full text-left text-sm px-3 py-2.5 bg-cream-100 border border-cream-400 hover:bg-forest-700 hover:text-cream-100 hover:border-forest-700 transition-colors"
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
      </aside>

      <section className="lg:col-span-2 bg-white border border-cream-400 flex flex-col min-h-[560px]">
        <div className="flex items-center justify-between px-5 py-3 border-b border-cream-400">
          <div className="text-sm text-forest-900/70">Conversa atual</div>
          <button
            type="button"
            onClick={reset}
            data-testid="ai-reset-button"
            className="text-xs flex items-center gap-1 text-forest-700 hover:text-copaiba-700"
          >
            <RotateCcw className="w-3.5 h-3.5" /> Nova conversa
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-4 bg-cream-50">
          {history.length === 0 && (
            <div className="text-forest-900/55 text-sm text-center pt-12">
              Escolha um atalho ao lado ou escreva sua solicitação abaixo.
            </div>
          )}
          {history.map((m, i) => (
            <div key={i} data-testid={`ai-message-${m.role}-${i}`}>
              <div className={`text-xs uppercase tracking-[0.18em] mb-1.5 ${m.role === "user" ? "text-forest-700" : "text-copaiba-700"}`}>
                {m.role === "user" ? "Você" : "Assistente"}
              </div>
              <div
                className={`p-4 text-sm leading-relaxed whitespace-pre-wrap ${
                  m.role === "user"
                    ? "bg-cream-200 border border-cream-400"
                    : "bg-white border border-forest-700/15"
                }`}
              >
                {m.content}
              </div>
              {m.role === "assistant" && (
                <button
                  type="button"
                  onClick={() => copy(m.content, i)}
                  data-testid={`ai-copy-${i}`}
                  className="mt-2 text-xs inline-flex items-center gap-1 text-forest-700 hover:text-copaiba-700"
                >
                  {copiedIdx === i ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                  {copiedIdx === i ? "Copiado" : "Copiar"}
                </button>
              )}
            </div>
          ))}
          {loading && (
            <div className="flex items-center gap-1 text-forest-700/70">
              <span className="w-2 h-2 rounded-full bg-forest-700 animate-pulse" />
              <span className="w-2 h-2 rounded-full bg-forest-700 animate-pulse" style={{ animationDelay: "120ms" }} />
              <span className="w-2 h-2 rounded-full bg-forest-700 animate-pulse" style={{ animationDelay: "240ms" }} />
              <span className="ml-2 text-xs">Gerando…</span>
            </div>
          )}
        </div>

        <div className="border-t border-cream-400 p-3 bg-white">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ex.: Escreva uma notícia sobre a colheita de copaíba deste ano…"
            rows={3}
            data-testid="ai-input"
            disabled={loading}
            className="w-full bg-cream-100 border border-cream-400 focus:border-forest-700 focus:outline-none p-3 text-sm resize-none"
          />
          <div className="flex justify-end mt-2">
            <button
              type="button"
              onClick={send}
              disabled={loading || !input.trim()}
              data-testid="ai-send-button"
              className="btn-primary !py-2.5 !px-5"
            >
              {loading ? "Gerando…" : (<>Gerar texto <Send className="w-4 h-4" /></>)}
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
